import React, { useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CompanyStatusBadge from './CompanyStatusBadge';
import CompanyTestChip from './CompanyTestChip';
import CompanyClosureChip from './CompanyClosureChip';
import { MARK_TEST_HINT, COMPANY_ZERO_TEST_HINT, canMarkCompanyTest } from './MarkCompanyTestDialog';
import { DELETE_LOCKED_HINT } from './DeleteCompanyDialog';
import { formatDate } from '../../../../utils/Utils';

/**
 * Formata uma data 'Y-m-d H:i:s' para uma string relativa curta em pt-BR (ex: "há 3 dias").
 * @param {string|null} dateStr
 * @returns {string}
 */
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return 'agora';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `há ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `há ${diffMonths} mês${diffMonths > 1 ? 'es' : ''}`;
  const diffYears = Math.floor(diffMonths / 12);
  return `há ${diffYears} ano${diffYears > 1 ? 's' : ''}`;
}

/**
 * Formata uma data 'Y-m-d H:i:s' (ou apenas data) usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * CompanyRow - Linha da tabela de empresas (Admin Geral)
 * @param {Object} props
 * @param {Object} props.company
 * @param {Function} props.onView
 * @param {Function} props.onToggleTest
 * @param {Function} props.onDelete
 */
function CompanyRow({ company, onView, onToggleTest, onDelete }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const {
    id,
    name,
    email,
    created_at,
    users_count,
    patients_count,
    appointments_count,
    last_login_at,
    last_activity_at,
    status,
    is_test,
    closure_state,
    closure_scheduled_at,
  } = company;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleRowClick = () => {
    if (onView) onView(id);
  };

  const handleView = () => {
    handleMenuClose();
    if (onView) onView(id);
  };

  const handleToggleTest = () => {
    handleMenuClose();
    if (onToggleTest) onToggleTest(company);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) onDelete(company);
  };

  const isTestCompany = Number(is_test) === 1;
  // Conta real só com a carência do encerramento vencida; o resto o servidor confere.
  const canOpenDelete = isTestCompany || closure_state === 'expired';
  const canMarkTest = canMarkCompanyTest(id, is_test);

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      onClick={handleRowClick}
    >
      {/* Empresa: id + nome + e-mail */}
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', mr: 0.75 }}>
            #{id}
          </Box>
          {name}
        </Typography>
        {email && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {email}
          </Typography>
        )}
      </TableCell>

      {/* Criada em */}
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {fmtDate(created_at)}
        </Typography>
      </TableCell>

      {/* Usuários */}
      <TableCell align="center">
        <Typography variant="body2">{users_count ?? 0}</Typography>
      </TableCell>

      {/* Pacientes */}
      <TableCell align="center">
        <Typography variant="body2">{patients_count ?? 0}</Typography>
      </TableCell>

      {/* Agendamentos */}
      <TableCell align="center">
        <Typography variant="body2">{appointments_count ?? 0}</Typography>
      </TableCell>

      {/* Último login */}
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {fmtDate(last_login_at)}
        </Typography>
        {last_login_at && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {timeAgo(last_login_at)}
          </Typography>
        )}
      </TableCell>

      {/* Última atividade */}
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {fmtDate(last_activity_at)}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <CompanyStatusBadge status={status} />
          <CompanyClosureChip state={closure_state} scheduledAt={closure_scheduled_at} />
        </Box>
      </TableCell>

      {/* Teste */}
      <TableCell>
        {Number(is_test) === 1 && <CompanyTestChip />}
      </TableCell>

      {/* Ações */}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Mais opções">
          <IconButton size="small" onClick={handleMenuOpen} sx={{ color: 'text.secondary' }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200 },
          }}
        >
          <MenuItem onClick={handleView} sx={{ fontSize: '0.875rem', gap: 1 }}>
            <VisibilityOutlinedIcon fontSize="small" />
            Ver detalhes
          </MenuItem>
          <Tooltip title={canMarkTest ? MARK_TEST_HINT : COMPANY_ZERO_TEST_HINT} placement="left">
            <span>
              <MenuItem onClick={handleToggleTest} disabled={!canMarkTest} sx={{ fontSize: '0.875rem', gap: 1 }}>
                <ScienceOutlinedIcon fontSize="small" />
                {Number(is_test) === 1 ? 'Desmarcar teste' : 'Marcar como teste'}
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={canOpenDelete ? '' : DELETE_LOCKED_HINT}>
            <span>
              <MenuItem
                onClick={handleDelete}
                disabled={!canOpenDelete}
                sx={{ fontSize: '0.875rem', gap: 1, color: '#dc2626' }}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
                Excluir empresa…
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export default CompanyRow;
